import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/pages/Login";
import { TID } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) {
      toast.error("Please accept the Privacy Policy to continue");
      return;
    }
    setBusy(true);
    try {
      const u = await register({ ...form, accept_privacy: true });
      toast.success(`Workspace ready — welcome, ${u.name}`);
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Create your REASON workspace</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Spin up a new tenant in seconds. You'll be the tenant admin.</p>
      <form onSubmit={submit} className="mt-7 space-y-4">
        <div>
          <Label className="text-xs">Your name</Label>
          <Input data-testid={TID.registerName} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Work email</Label>
          <Input data-testid={TID.registerEmail} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Password (min 8 chars)</Label>
          <Input data-testid={TID.registerPassword} type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div className="flex items-start gap-2 pt-1">
          <Checkbox id="accept-privacy" checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="register-accept-privacy" className="mt-0.5" />
          <Label htmlFor="accept-privacy" className="text-xs font-normal text-muted-foreground leading-relaxed cursor-pointer">
            I have read and agree to the{" "}
            <Link to="/privacy-policy" target="_blank" className="text-primary font-medium hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
          </Label>
        </div>
        <Button data-testid={TID.registerSubmit} type="submit" disabled={busy || !agree} className="w-full bg-primary hover:bg-primary/90">
          {busy ? "Provisioning…" : "Create workspace"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        Already onboarded? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
